import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Shield, Home, Wrench, Calendar } from 'lucide-react';
import GoogleMap from '@/components/Maps/GoogleMap';

const GreenvilleNCPage = () => {
  const businessAddress = "1414 Forbes St, Greenville, NC 27858";

  return (
    <Layout>
      <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              🏠 Carolina Trophy Roofs Greenville: Premium Roofing with Trusted Brands & Proven Performance
            </h1>
            <div className="space-y-2 text-lg">
              <p className="flex items-center justify-center">
                <MapPin className="h-5 w-5 text-primary mr-2" />
                <strong>1414 Forbes St, Greenville, NC 27858</strong>
              </p>
              <p className="flex items-center justify-center">
                <Phone className="h-5 w-5 text-primary mr-2" />
                <strong>(252) 371-2266</strong>
              </p>
              <p className="text-gray-600 italic">
                Pitt County's trusted roofing contractor since 2015, serving Greenville, Winterville, Ayden, Farmville, and surrounding Eastern NC communities.
              </p>
            </div>
          </div>

          {/* Expert Roofing Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Shield className="h-8 w-8 text-primary mr-3" />
              Expert Roofing for Greenville's Climate—Backed by Leading Brands
            </h2>
            <p className="text-lg mb-6">
              When storms, heat, and humidity threaten your home, your roof deserves more than basic protection. 
              At Carolina Trophy Roofs Greenville, we build every roof with <strong>nationally trusted materials</strong> and <strong>region-specific expertise</strong>.
            </p>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">🧰 Preferred Material Brands</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <p>
                      • <a href="https://www.iko.com/na/residential-roofing-products/shingles/dynasty/" className="text-blue-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">IKO Dynasty® Shingles</a> – Advanced weather protection with ArmourZone® reinforcement
                    </p>
                    <p>
                      • <a href="https://www.atlasroofing.com/roof-shingles/pinnacle-pristine" className="text-blue-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">Atlas Pinnacle® Pristine</a> – Featuring Scotchgard™ to resist algae streaks
                    </p>
                    <p>
                      • <a href="https://www.certainteed.com/residential-roofing/products/landmark/" className="text-blue-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">CertainTeed Landmark® Series</a> – Dual-layer design with Class A fire rating
                    </p>
                  </div>
                  <div className="space-y-3">
                    <p>
                      • <a href="https://www.owenscorning.com/en-us/roofing/shingles/trudefinition-duration" className="text-blue-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">Owens Corning Duration® Shingles</a> – Built with SureNail® technology for superior wind resistance
                    </p>
                    <p>
                      • <a href="https://www.gaf.com/en-us/roofing-products/residential-roofing-products/shingles/timberline/hdz" className="text-blue-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">GAF Timberline® HDZ</a> – America's #1-selling shingle with LayerLock™
                    </p>
                    <p>
                      • <a href="https://www.amesresearch.com/" className="text-blue-600 hover:underline font-medium" target="_blank" rel="noopener noreferrer">Ames Roof Coatings</a> – Elastomeric coatings for commercial and specialty roof systems
                    </p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    🔗 All links above go directly to manufacturer pages for product specs, warranty details, and technical documentation.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Residential Roofing Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Home className="h-8 w-8 text-primary mr-3" />
              Residential Roofing Built to Last
            </h2>
            <p className="text-lg mb-6">
              Whether you're in a historic Greenville home or new construction in Winterville, we match our material and methods to your property and climate zone:
            </p>
            
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Services Include:</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2">
                    <li>• Roof replacement with architectural & designer shingles</li>
                    <li>• Metal roof installation (standing seam and exposed fastener)</li>
                    <li>• Precision repairs for storm, wind, or hail damage</li>
                  </ul>
                  <ul className="space-y-2">
                    <li>• Energy-efficient roofing with radiant barriers & cool roof options</li>
                    <li>• Full attic ventilation upgrades</li>
                  </ul>
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm">
                    📚 <a href="https://www.homeadvisor.com/cost/roofing/install-a-roof/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">HomeAdvisor</a> reports national average replacement costs of $9,516–$11,719—<strong>we help you get maximum life and performance</strong> from your investment.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Commercial & Flat Roofing Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6">🏢 Commercial & Flat Roofing</h2>
            <p className="text-lg mb-6">
              Greenville's commercial buildings require performance-grade roofing systems built for heat, moisture, and low-slope challenges:
            </p>
            
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2">
                    <li>• <a href="https://www.buildings.com/articles/27356/what-you-need-know-about-tpo-roofing" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">TPO Roofing</a> – Reflective, weldable, energy-saving membranes</li>
                    <li>• <a href="https://www.nrca.net/Technical/Architectural-Details/EPDM" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">EPDM Systems</a> – Durable, flexible, and ideal for Eastern NC's heat swings</li>
                  </ul>
                  <ul className="space-y-2">
                    <li>• Modified Bitumen and Built-Up Roofs (BUR)</li>
                    <li>• Ames Roof Coatings for commercial restoration</li>
                    <li>• Gutter & drainage system integration</li>
                  </ul>
                </div>
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm">
                    📈 <a href="https://ibhs.org/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">IBHS</a> data shows routine commercial maintenance can cut emergency repair costs by up to 70%.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Emergency Services Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6">🌀 Emergency Roofing Services for Storm Damage</h2>
            <p className="text-lg mb-6">
              With NOAA reporting hurricanes and tropical systems affecting Pitt County <strong>every 3–4 years</strong> (<a href="https://www.nhc.noaa.gov/data/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">NOAA Historical Hurricanes</a>), we're always ready:
            </p>
            
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2">
                    <li>• 24/7 storm response</li>
                    <li>• Emergency tarping and stabilization</li>
                    <li>• Drone-based damage assessment</li>
                  </ul>
                  <ul className="space-y-2">
                    <li>• Full insurance claim support</li>
                    <li>• Priority scheduling for Greenville-area emergencies</li>
                  </ul>
                </div>
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm">
                    📷 All inspections include thermal + drone imaging for detailed reports.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Specialty Services Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <Wrench className="h-8 w-8 text-primary mr-3" />
              Specialty Roofing Services
            </h2>
            
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <p>• Church steeple restoration & historic slate replacement (<a href="https://savingplaces.org/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">National Trust for Historic Preservation</a>)</p>
                  <p>• Copper, lead, and custom flashing work</p>
                  <p>• Solar-ready roofing & smart attic systems</p>
                  <p>• Ames cool roof coatings for UV and waterproofing protection</p>
                </div>
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm">
                    <a href="https://coolroofs.org/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Cool Roof Rating Council</a> confirms coatings like Ames' reduce rooftop temps by up to 40°F—cutting A/C use significantly.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Climate-Specific Design Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6">🌡️ Roofing Systems Designed for Greenville's Climate</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">✅ Coastal Wind & Rain</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li>• Shingles rated up to <strong>130–150 mph</strong></li>
                    <li>• FORTIFIED roofing methods available (<a href="https://fortifiedhome.org/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">FORTIFIED Home Program</a>)</li>
                    <li>• Double-underlayment, drip edge, and hurricane clips</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">✅ Humidity & Vapor Control</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li>• Algae-resistant shingles (Atlas, GAF, Owens Corning)</li>
                    <li>• Smart ventilation & vapor barrier integration</li>
                    <li>• Rot-resistant decking & flashing</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">✅ Thermal Cycling</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    <li>• Shingle systems with flexible mat cores and high-heat granules</li>
                    <li>• Built-in heat reflectivity with Atlas and GAF Cool Roofs</li>
                    <li>• Class A fire resistance from CertainTeed and IKO</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Insurance & Financing Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6">💼 Insurance, Financing & Compliance</h2>
            
            <Card>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <p>• We work directly with <strong>USAA, State Farm, NCIUA, Allstate</strong>, and more</p>
                    <p>• Free inspections & storm documentation for all claims</p>
                    <p>• 0% financing options through <strong>local partners and manufacturer programs</strong></p>
                  </div>
                  <div className="space-y-3">
                    <p>• <strong>Pitt County code-compliant</strong> with enhanced wind zone certifications</p>
                    <p>• Full inspection, permits, and warranty registration handled by our team</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Warranty Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6">🏅 Warranty Coverage You Can Trust</h2>
            
            <Card>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2">
                    <li>• Manufacturer product warranties up to <strong>50 years</strong></li>
                    <li>• Carolina Trophy Roofs <strong>5-10-Year Labor Warranty</strong></li>
                  </ul>
                  <ul className="space-y-2">
                    <li>• Full-system warranties</li>
                    <li>• Storm damage priority response for warranty clients</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Resources Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6">🔍 Want to Learn More? Trusted Resources:</h2>
            
            <Card>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2">
                    <li>• <a href="https://www.gaf.com/en-us/roofing-learning-center" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">GAF Roof Learning Center</a></li>
                    <li>• <a href="https://www.iko.com/blog/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">IKO Roofing Blog</a></li>
                    <li>• <a href="https://www.atlasroofing.com/pro" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Atlas Pro Resources</a></li>
                  </ul>
                  <ul className="space-y-2">
                    <li>• <a href="https://www.certainteed.com/resources/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">CertainTeed Technical Library</a></li>
                    <li>• <a href="https://www.owenscorning.com/en-us/roofing/science" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Owens Corning Roofing Science</a></li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Google Map Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 flex items-center">
              <MapPin className="h-8 w-8 text-primary mr-3" />
              Find Us in Greenville, NC
            </h2>
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Our Location</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <MapPin className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold">Address</h3>
                        <p className="text-gray-600">{businessAddress}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Phone className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold">Phone</h3>
                        <p className="text-gray-600">(252) 371-2266</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Mail className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold">Email</h3>
                        <p className="text-gray-600">
                          <a href="mailto:claims@carolinatrophyroofs.com" className="hover:underline">
                            claims@carolinatrophyroofs.com
                          </a>
                        </p>
                      </div>
                    </div>
                    <div className="pt-4">
                      <p className="text-sm text-gray-600">
                        <strong>Service Area:</strong> Serving Greenville, Winterville, Ayden, Farmville, and all of Pitt County, NC
                      </p>
                    </div>
                  </div>
                  <div>
                    <GoogleMap 
                      address={businessAddress}
                      businessName="Carolina Trophy Roofs Greenville"
                      className="w-full h-80 rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Call to Action Section */}
          <section className="text-center bg-brand-carolina-blue text-white p-8 rounded-lg">
            <h2 className="text-3xl font-bold mb-4 flex items-center justify-center">
              <Calendar className="h-8 w-8 mr-3" />
              Schedule Your Free Greenville Roof Inspection Today
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8 text-left">
              <div className="text-center">
                <MapPin className="h-8 w-8 mx-auto mb-2" />
                <h3 className="font-bold mb-1">Visit Us:</h3>
                <p>1414 Forbes St, Greenville, NC 27858</p>
              </div>
              <div className="text-center">
                <Phone className="h-8 w-8 mx-auto mb-2" />
                <h3 className="font-bold mb-1">Call/Text:</h3>
                <p>+1 (252) 371-2266</p>
              </div>
              <div className="text-center">
                <Mail className="h-8 w-8 mx-auto mb-2" />
                <h3 className="font-bold mb-1">Email:</h3>
                <p>
                  <a href="mailto:claims@carolinatrophyroofs.com" className="hover:underline">
                    claims@carolinatrophyroofs.com
                  </a>
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-bold mb-4">✅ Includes:</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 text-left">
                <p>• Full shingle & substrate inspection</p>
                <p>• Drone & moisture scan report</p>
                <p>• Brand & system recommendations</p>
                <p>• Warranty and ROI analysis</p>
                <p>• Insurance claim assistance if needed</p>
              </div>
            </div>

            <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-brand-safety-orange text-white hover:bg-brand-safety-orange/90">
                  Get Your Free Inspection
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="bg-white text-brand-carolina-blue border-white hover:bg-gray-100 hover:text-brand-carolina-blue">
                <Phone className="h-5 w-5 mr-2" />
                Call (252) 371-2266
              </Button>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default GreenvilleNCPage;
